/**
 * Payroll & Staff Hours — dedicated route wrapping the existing
 * <PayrollWidget> component. All business logic (scheduled/worked/payable/
 * overtime hours, estimated labour cost, incomplete-attendance confirm,
 * Confirm Week / rate snapshot, previous periods, CSV/XLSX export, history)
 * lives inside PayrollWidget itself and is reused verbatim — this page just
 * adds a route, header and week/site picker.
 *
 * Access: admin / manager (and manager-tier roles). Care worker → blocked stub.
 */
import React, { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import PayrollWidget from "@/components/PayrollWidget";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Info, CalendarClock } from "lucide-react";
// Timezone-safe date helpers — do NOT reintroduce `.toISOString().slice(0, 10)`
// here. See lib/dateWeek.js and lib/__tests__/dateWeek.test.js for the fix
// history (the payroll week-picker was sending Sun instead of Mon in UTC+
// browsers, so only 1 of 5 shifts reached payroll).
import { mondayOf, addDays } from "@/lib/dateWeek";

const MANAGER_ROLES = ["admin", "manager", "registered_manager",
  "deputy_manager", "service_manager", "super_admin"];

export default function Payroll() {
  const { user } = useAuth();
  const role = (user?.role || "").toLowerCase();
  const canManage = MANAGER_ROLES.includes(role);

  const [weekStart, setWeekStart] = useState(() => mondayOf());
  const [filterSite, setFilterSite] = useState("all");
  const [sites, setSites] = useState([]);

  useEffect(() => {
    if (!canManage) return;
    api.get("/sites").then((r) => setSites(r.data || [])).catch(() => setSites([]));
  }, [canManage]);

  if (!canManage) {
    return (
      <div className="p-6" data-testid="payroll-page-blocked">
        <div className="max-w-lg mx-auto border border-slate-200 bg-white rounded-lg p-6 mt-10 text-center">
          <div className="text-slate-400 mb-2 flex justify-center"><Info className="w-6 h-6" /></div>
          <div className="font-semibold text-slate-900">Manager / Admin access only</div>
          <div className="text-sm text-slate-600 mt-1">
            Payroll & Staff Hours is limited to managers and admins.
          </div>
        </div>
      </div>
    );
  }

  const weekEnd = addDays(weekStart, 6);

  return (
    <div className="p-4 sm:p-6 space-y-6" data-testid="payroll-page">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">
            Workforce · Finance
          </div>
          <h1 className="text-2xl sm:text-3xl font-heading font-semibold text-slate-900 mt-1">
            Payroll &amp; Staff Hours
          </h1>
          <p className="text-sm text-slate-600 mt-1 max-w-2xl">
            Review worked hours, confirm payable time and prepare approved staff hours for payroll.
          </p>
        </div>
      </div>

      {/* Week + site picker */}
      <div className="border border-slate-200 rounded-lg bg-white p-4 grid grid-cols-1 sm:grid-cols-3 gap-3"
           data-testid="payroll-filters">
        <div>
          <Label className="text-[11px] uppercase tracking-wider text-slate-600 font-semibold">Payroll week</Label>
          <div className="mt-1 flex items-center gap-1">
            <Button variant="outline" size="sm" onClick={() => setWeekStart(mondayOf(addDays(weekStart, -7)))}
                    data-testid="payroll-week-prev">‹</Button>
            <Input type="date" value={weekStart}
                   onChange={(e) => setWeekStart(mondayOf(e.target.value))}
                   className="flex-1"
                   data-testid="payroll-week-input" />
            <Button variant="outline" size="sm" onClick={() => setWeekStart(mondayOf(addDays(weekStart, 7)))}
                    data-testid="payroll-week-next">›</Button>
          </div>
          <div className="text-[10px] text-slate-500 mt-0.5">
            {weekStart} → {weekEnd} · snapped to Monday
          </div>
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-wider text-slate-600 font-semibold">Site</Label>
          <Select value={filterSite} onValueChange={setFilterSite}>
            <SelectTrigger className="mt-1" data-testid="payroll-site-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All sites</SelectItem>
              {sites.map((s) => (
                <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-end">
          <div className="text-[11px] text-slate-500 inline-flex items-center gap-1.5">
            <CalendarClock className="w-3.5 h-3.5 text-slate-400" />
            Historical rate snapshots on approved periods are never recomputed.
          </div>
        </div>
      </div>

      {/* Reuses the existing widget — same functions as on Staff Schedule */}
      <PayrollWidget
        weekStart={weekStart}
        weekEnd={weekEnd}
        filterSite={filterSite}
        user={user}
        canManage={canManage}
      />
    </div>
  );
}
